import Modal from "@material-ui/core/Modal";
import { CustomIcon } from "components/icon/customIcon.component";
import { Text } from "components/Typography/text.styled";
import { Color } from "config/colors";
import { FontFamily, FontSize } from "config/font";
import { MonthWorkingHours } from "model/Salary";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Api } from "services/api/api.service";
import { useAlertStore } from "stores/alertStore/alertStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { getMonthNameFromPeriod } from "web-apps/company/utils/utils";
import { IconCircularContainer } from "../dashboard/dashboard.styled";
import {
  ArrowSign,
  YearSelectContainer,
} from "../timeReport/monthlyOverview.styled";
import {
  EditHoursModalBody,
  HoursActionContainer,
  Input,
  LinkWrapper,
  MonthContainer,
  MonthHoursContainer,
  MonthListItem,
} from "./salary.styled";

type Props = {
  setEditWorkingHours: React.Dispatch<React.SetStateAction<boolean>>;
  editWorkingHours: boolean;
};

export const EditWorkingHoursModal: React.FC<Props> = ({
  setEditWorkingHours,
  editWorkingHours,
}) => {
  const { t } = useTranslation();
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [monthData, setMonthData] = useState<MonthWorkingHours[]>([]);
  const [{ company }] = useCompanyStore();
  const [, , alertDispatch] = useAlertStore();
  const [selectedMonth, setSelectedMonth] = useState<string>();

  const api = useMemo(() => Api(), []);

  const companyId = company?.id;

  const handleNextYear = useCallback(() => {
    setCurrentYear((prevYear) => prevYear + 1);
  }, []);

  const handlePreviousYear = useCallback(() => {
    setCurrentYear((prevYear) => prevYear - 1);
  }, []);

  const handleModalClose = useCallback(() => {
    setEditWorkingHours(false);
  }, [setEditWorkingHours]);

  const handleHoursChange = useCallback((period: string, value: string) => {
    const newValue = value === "" ? 0 : parseInt(value);

    setMonthData((prevMonthData) =>
      prevMonthData.map((month) =>
        month.period === period ? { ...month, hours: newValue } : month
      )
    );
  }, []);

  const overrideMonthWorkingHours = useCallback(
    (period: string) => {
      if (!companyId) return;

      const monthHours = monthData.find((m) => m.period === period)?.hours || 0;

      api.company.salary
        .overrideMonthWorkingHours(companyId, period, monthHours)
        .then(() => {
          alertDispatch({
            type: "SHOW_ALERT",
            payload: {
              icon: "checkmark",
              title: t("Salary.Changed"),
              message: t("Salary.ChangedWorkingHours"),
            },
          });
        })
        .catch(() => {
          alertDispatch({
            type: "SHOW_ALERT",
            payload: {
              icon: "alert",
              title: t("Alerts.OhNo"),
              message: t("Alerts.SomethingWentWrong"),
            },
          });
        });
    },
    [api, companyId, monthData, alertDispatch, t]
  );

  useEffect(() => {
    if (!companyId) return;

    let isMounted = true;

    api.company.salary
      .getCompanyWorkHours(companyId, currentYear)
      .then((res) => {
        if (isMounted) {
          setMonthData(res.data);
        }
      })
      .catch((e) => {
        console.error("Error when getting month hours", e);
      });

    return () => {
      isMounted = false;
    };
  }, [currentYear, companyId, api]);

  const monthList = useMemo(
    () =>
      monthData.map((month, index) => (
        <MonthListItem key={month.period || index}>
          <MonthContainer>
            <Text
              style={{ margin: 0, padding: 0 }}
              fontSize={FontSize.Small}
              color={Color.SeaBlue600}
            >
              {t("Salary.Month")}
            </Text>
            <Text
              color={Color.MidnightBlue}
              fontFamily={FontFamily.MontserratRegular}
            >
              {getMonthNameFromPeriod(month.period)}
            </Text>
          </MonthContainer>
          <MonthHoursContainer>
            <HoursActionContainer>
              <Text
                style={{ marginBottom: 5, padding: 0 }}
                fontSize={FontSize.Small}
                color={Color.SeaBlue600}
              >
                {t("Salary.Hours")}
              </Text>
              <Input
                type="text"
                maxLength={3}
                placeholder="0"
                value={month.hours}
                onChange={(e) =>
                  handleHoursChange(month.period, e.target.value)
                }
                style={{ marginBottom: 20, width: 100 }}
                onClick={() => setSelectedMonth(month.period)}
              />
            </HoursActionContainer>
            {selectedMonth === month.period && (
              <IconCircularContainer
                isAccept
                onClick={() => {
                  overrideMonthWorkingHours(month.period);
                  setSelectedMonth(undefined);
                }}
                style={{ marginLeft: 20, marginTop: 20 }}
              >
                <CustomIcon
                  className="icon"
                  name="checkmark"
                  size="24px"
                  padding="2px"
                  color={Color.White}
                />
              </IconCircularContainer>
            )}
          </MonthHoursContainer>
        </MonthListItem>
      )),
    [monthData, t, handleHoursChange, selectedMonth, overrideMonthWorkingHours]
  );

  const modalHeader = useMemo(
    () => (
      <>
        <Text
          fontFamily={FontFamily.MontserratSemiBold}
          color={Color.PitchBlack}
          fontSize={FontSize.H3}
          style={{ textAlign: "center" }}
        >
          {t("Salary.MonthHours")}
        </Text>
        <YearSelectContainer>
          {currentYear > new Date().getFullYear() && (
            <ArrowSign onClick={handlePreviousYear}>&#60;</ArrowSign>
          )}
          <Text
            fontSize={FontSize.H4}
            fontFamily={FontFamily.MontserratSemiBold}
            style={{ margin: 0 }}
          >
            {currentYear}
          </Text>
          {currentYear < new Date().getFullYear() + 2 && (
            <ArrowSign onClick={handleNextYear}>&#62;</ArrowSign>
          )}
        </YearSelectContainer>
      </>
    ),
    [currentYear, t, handlePreviousYear, handleNextYear]
  );

  return (
    <Modal open={editWorkingHours} onClose={handleModalClose}>
      <EditHoursModalBody>
        <LinkWrapper>
          <CustomIcon
            color={Color.LighterDestructive}
            onClick={handleModalClose}
            name="cross"
          >
            {t("General.Close")}
          </CustomIcon>
        </LinkWrapper>
        {modalHeader}
        {monthList}
      </EditHoursModalBody>
    </Modal>
  );
};
